import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, ExternalLink, CheckCircle2, Circle, Clock } from "lucide-react";
import { VideoBackground } from "@/components/VideoBackground";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Sky Lofi ATC | Lofi Hip Hop Beats with Live Air Traffic Control Radio</title>
        <meta 
          name="description" 
          content="Sky Lofi ATC combines relaxing lofi hip hop beats with live air traffic control radio from airports worldwide. Free 24/7 streaming for studying, coding, working, and focus. No ads, no interruptions." 
        />
        <meta name="keywords" content="lofi atc, lofi air traffic control, study music, focus music, lofi hip hop, chill beats, airport radio, ATC radio stream, concentration music, coding music, work music, relaxation music, ambient music, lo-fi beats, study playlist, productivity music" />
        <link rel="canonical" href="https://skylofiatc.com/about" />
        
        <meta property="og:title" content="About Sky Lofi ATC | Lofi Beats + Live Air Traffic Control" />
        <meta property="og:description" content="The story behind the internet's favorite lofi + ATC radio. Free, ad-free, built for focus and deep work." />
        <meta property="og:url" content="https://skylofiatc.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://skylofiatc.com/og-image.jpg" />
        
        <meta name="twitter:title" content="About Sky Lofi ATC" />
        <meta name="twitter:description" content="The story behind the internet's favorite lofi + ATC radio. Free, ad-free, built for focus." />
        <meta name="twitter:image" content="https://skylofiatc.com/og-image.jpg" />
      </Helmet>

      <VideoBackground
        storageKey="background-video.mp4"
        title="Ambient airplane flying above clouds - Sky Lofi ATC background video"
      />

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 md:p-8">
        <article className="glass-card w-full max-w-2xl rounded-2xl p-6 md:p-8">
          <header className="flex items-center justify-between mb-6">
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Player
              </Button>
            </Link>
          </header>

          <ScrollArea className="h-[calc(100vh-16rem)] md:h-[calc(70vh-4rem)]">
            <div className="space-y-8 pr-4">
              <section>
                <h1 className="text-2xl md:text-3xl font-bold mb-4">About Sky Lofi ATC</h1>
                <p className="text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Sky Lofi ATC</strong> is a free streaming service that combines 
                  relaxing <strong>lofi hip hop beats</strong> with live <strong>air traffic control radio</strong> from 
                  airports around the world. It's the perfect background audio for studying, coding, working, or just chilling.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  Remember lofiatc.com? That beloved site went dark in 2024. I rebuilt it from scratch — but better, 
                  with cinematic video backgrounds and a modern, distraction-free interface.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">Why Lofi + ATC?</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The combination of calm, repetitive <strong>lofi music</strong> with ambient <strong>air traffic control chatter</strong> creates 
                  a unique soundscape that many find perfect for <strong>concentration</strong> and <strong>deep work</strong>. 
                  The music provides a steady rhythm while the occasional radio transmissions add just enough 
                  variation to keep your brain engaged without being distracting.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  It's become a favorite for <strong>students</strong>, <strong>developers</strong>, <strong>writers</strong>, and anyone 
                  who needs <strong>focus music</strong> that isn't completely silent or too stimulating.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">Features</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span>✈️</span>
                    <span><strong className="text-foreground">Live ATC feeds</strong> from major international airports (LAX, JFK, SFO, ORD, and more)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>🎵</span>
                    <span><strong className="text-foreground">Curated lofi hip hop</strong> playlist optimized for focus and relaxation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>🎬</span>
                    <span><strong className="text-foreground">Cinematic video backgrounds</strong> of planes in flight</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>🔊</span>
                    <span><strong className="text-foreground">Independent volume controls</strong> for music and ATC audio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>📱</span>
                    <span><strong className="text-foreground">Works on desktop and mobile</strong> browsers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>🚫</span>
                    <span><strong className="text-foreground">Zero ads, zero tracking</strong> — just press play</span>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-4">Roadmap</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Sky Lofi ATC is actively developed. Here's what's coming:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-muted-foreground">Multiple airport ATC feeds</span>
                    <Badge variant="secondary" className="text-xs">Done</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-muted-foreground">Video backgrounds</span>
                    <Badge variant="secondary" className="text-xs">Done</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-muted-foreground">Mobile-responsive design</span>
                    <Badge variant="secondary" className="text-xs">Done</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-muted-foreground">Pomodoro timer integration</span>
                    <Badge variant="secondary" className="text-xs">Done</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                    <span className="text-foreground">Live airport webcam backgrounds</span>
                    <Badge variant="outline" className="text-xs">In Progress</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <span className="text-muted-foreground">Ambient sound effects (rain, cabin noise, thunder)</span>
                    <Badge variant="outline" className="text-xs">Planned</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <span className="text-muted-foreground">More music genres (jazz lofi, ambient, classical)</span>
                    <Badge variant="outline" className="text-xs">Planned</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <span className="text-muted-foreground">Save favorite presets & settings</span>
                    <Badge variant="outline" className="text-xs">Planned</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <span className="text-muted-foreground">User accounts & sync across devices</span>
                    <Badge variant="outline" className="text-xs">Exploring</Badge>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">Perfect For</h2>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Studying</Badge>
                  <Badge variant="secondary">Coding</Badge>
                  <Badge variant="secondary">Writing</Badge>
                  <Badge variant="secondary">Working from home</Badge>
                  <Badge variant="secondary">Deep focus sessions</Badge>
                  <Badge variant="secondary">Pomodoro technique</Badge>
                  <Badge variant="secondary">Background noise</Badge>
                  <Badge variant="secondary">Relaxation</Badge>
                  <Badge variant="secondary">Aviation enthusiasts</Badge>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">Support the Project</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Sky Lofi ATC is <strong>free and ad-free forever</strong>. But servers and bandwidth cost money. 
                  If you use this regularly and want to help keep it running, consider buying me a coffee.
                </p>
                <Button
                  variant="outline"
                  className="mt-4 glass-card hover:scale-105 smooth-transition"
                  onClick={() => window.open("https://buymeacoffee.com/skylofiatc", "_blank")}
                >
                  <Heart className="h-4 w-4 mr-2 text-red-500" />
                  Support on Buy Me a Coffee
                  <ExternalLink className="h-3 w-3 ml-2 opacity-50" />
                </Button>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">Get in Touch</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Have an airport request? Feature idea? Bug report? I'm one person building this for the community, 
                  and I'd love to hear from you. Use the contact form on the <Link to="/" className="text-primary hover:underline">main page</Link>.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Clear skies ✈️
                </p>
              </section>
            </div>
          </ScrollArea>
        </article>
      </div>
    </>
  );
};

export default About;
